// CSR-1855 — single shared asset-identity helper, importable by BOTH the server
// (sync merge) and the client bundle (the Loss Payee picker) via `@shared/*`.
// `keyForVehicle` + `canonicalKey` were moved here VERBATIM from
// `server/asset-merge.ts` (which now imports/re-exports them) so there is ONE
// precedence definition, per the CLAUDE.md "don't duplicate consistency-critical
// logic" rule. Type-only import of `PolicyVehicle` keeps this module pure (no
// runtime dependency on the Drizzle schema), so it is safe in the client bundle.
import type { PolicyVehicle } from "./schema";

/**
 * Identity for vehicles and trailers: `databaseId` (DC's per-asset UUID) is
 * the most reliable. Falls back to VIN, then to a canonical content hash for
 * VIN-less entries (e.g. Non-Owned Attached Trailers).
 */
export function keyForVehicle(v: PolicyVehicle): string {
  if (v.databaseId && typeof v.databaseId === "string") {
    return `id:${v.databaseId.trim().toLowerCase()}`;
  }
  if (v.vin && typeof v.vin === "string") {
    const normalized = v.vin.trim().toUpperCase();
    if (normalized) return `vin:${normalized}`;
  }
  return `content:${canonicalKey(v)}`;
}

/**
 * Build a stable key from an object's content, excluding lifecycle stamps so
 * the same physical asset doesn't get a different content key just because
 * its `addedAt`/`removedAt` changed.
 */
export function canonicalKey(obj: object): string {
  const source = obj as Record<string, unknown>;
  const filtered: Record<string, unknown> = {};
  for (const k of Object.keys(source).sort()) {
    if (k === "addedAt" || k === "removedAt") continue;
    filtered[k] = source[k];
  }
  return JSON.stringify(filtered);
}

/**
 * CSR-1855 — every identity form an asset could be keyed under, in the same
 * precedence order as `keyForVehicle`. Used at READ time to resolve a stored
 * `asset_key` against a live asset regardless of which precedence tier produced
 * the stored key: a legacy `vin:`-keyed link still matches an asset that has
 * since gained a `databaseId` (whose primary key is now `id:`), so the link
 * never silently drops.
 *
 * The `id:` form is pushed under the SAME guard/expression as `keyForVehicle`
 * (no extra empty-guard), the `vin:` form when the VIN trims non-empty, and the
 * `content:` form always — so the invariant `keyForVehicle(v) ∈
 * assetIdentityForms(v)` holds for all inputs.
 */
export function assetIdentityForms(v: PolicyVehicle): string[] {
  const forms: string[] = [];
  if (v.databaseId && typeof v.databaseId === "string") {
    forms.push(`id:${v.databaseId.trim().toLowerCase()}`);
  }
  if (v.vin && typeof v.vin === "string") {
    const normalized = v.vin.trim().toUpperCase();
    if (normalized) forms.push(`vin:${normalized}`);
  }
  forms.push(`content:${canonicalKey(v)}`);
  return forms;
}

/**
 * CSR-1855 — true when `key` matches ANY of the asset's identity forms. Bridges
 * write-time↔read-time precedence drift (see `assetIdentityForms`).
 */
export function assetMatchesKey(v: PolicyVehicle, key: string): boolean {
  return assetIdentityForms(v).includes(key);
}
